import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import { Spinner } from '@components/Spinner';
import Tooltip from '@components/Tooltip';
import { Button } from '@components/ui/button';
import type { EvalBudgetStatus, EvalFinding, EvalRunDetail, EvalRunSummary } from '@queries/evalRuns';
import { cn } from 'cn';
import { Eye, EyeOff, LoaderCircle, X } from 'lucide-react';

import { AdviceNotice } from '../components/AdviceNotice';
import { ChecklistSection } from '../components/ChecklistSection';
import { FindingCard } from '../components/FindingCard';
import type { FindingsFilter } from '../components/FindingsToolbar';
import { FindingsToolbar } from '../components/FindingsToolbar';
import { IntroPanel } from '../components/IntroPanel';
import { RecheckProgress } from '../components/RecheckProgress';
import { ResolutionCheck } from '../components/ResolutionCheck';
import { RunSwitcher } from '../components/RunSwitcher';
import { ScanProgress } from '../components/ScanProgress';
import { StartEvalRunForm } from '../components/StartEvalRunForm';
import { TierCountPills } from '../components/TierCountPills';
import type { FindingTier } from '../tiers';
import { TIER_BY_SEVERITY, TIER_LABEL, TIER_ORDER, tierClasses } from '../tiers';
import type { EvalConfigOption, EvalFindingGrade, EvalFindingResolution } from '../types';
import { RUN_IS_TERMINAL, SEVERITY_ORDER } from '../types';
import { buildChecklistRegisters } from '../utils/checklistRegisters';
import { formatDateTime } from '../utils/formatDateTime';
import { isAssessableFinding } from '../utils/openFindings';
import type { GuidanceBlock } from '../utils/readiness';
import { describeRerunBlock } from '../utils/rerun';

interface PbqaEvalViewProps {
  runs: EvalRunSummary[];
  runsLoading: boolean;
  detail?: EvalRunDetail;
  detailLoading: boolean;
  /** True when the selected run's detail fetch failed — the drawer must not spin forever. */
  detailError?: boolean;
  onRetryDetail?: () => void;
  configs: EvalConfigOption[];
  configsLoading: boolean;
  /** The config the broker-view run button uses; brokers only ever see its runs. */
  championConfigId?: string;
  /** Today's token budget for the caller; `undefined` while loading or on error (never blocks). */
  budget?: EvalBudgetStatus;
  /**
   * Why no guidance scan can run on this contract right now — wrong stage,
   * incomplete form, budget spent. Blocks the intro's run button and the
   * results' re-run alike; `undefined` when a scan may go ahead.
   */
  guidanceBlock?: GuidanceBlock;
  /** True once the contract has left Draft / Review: suggestions can be read but no longer actioned. */
  isLocked?: boolean;
  /** False off the Contract View, where "Scroll to Block" has nothing on screen to scroll to. */
  canFocusBlock?: boolean;
  activeRunId?: string;
  activeBlockId?: string;
  isStarting: boolean;
  isCancelling: boolean;
  isGrading: boolean;
  isResolving: boolean;
  isStartingResolutionCheck: boolean;
  /** True with the admin tools on: run switcher, config picker, grading, duplicates, checklist. */
  showEvalControls: boolean;
  /** True when the caller is internal staff, entitled to the admin-tools toggle. */
  canToggleAdminMode?: boolean;
  isAdminMode?: boolean;
  onToggleAdminMode?: () => void;
  onClose: () => void;
  onSelectRun: (runId: string) => void;
  onStart: (evalConfigId?: string, evalConfigVersionNumber?: number) => void;
  onCancel: (runId: string) => void;
  /** "Re-run guidance scan": the AI re-check of which open suggestions the edits resolved. */
  onStartResolutionCheck: (runId: string) => void;
  onGrade: (runId: string, findingId: string, grade: EvalFindingGrade | null, note?: string) => void;
  /** `resolution: null` reopens a resolved / dismissed finding. */
  onResolve: (runId: string, findingId: string, resolution: EvalFindingResolution | null, note?: string) => void;
  onFocusBlock: (blockId: string | undefined) => void;
  /** Block id → name, for resolving inline `[block:<id>]` references in finding text. */
  blockNameById: Map<string, string>;
  /** Section id → label, for resolving inline `[section:<id>]` references. */
  sectionNameById?: Map<string, string>;
}

interface TierGroup {
  tier: FindingTier;
  /** Findings shown under this heading after the review-state filter. */
  items: EvalFinding[];
  /** All findings of this tier, before the filter — the "N found / N left / done" pill. */
  total: number;
  open: number;
}

const footerClasses = 'flex flex-col gap-3 border-t border-gray-200 bg-white px-4 py-4';

/**
 * Findings the consolidation pass marked as duplicates are hidden by default;
 * the internal "Show duplicates" toggle reveals them. Within the list, ties in
 * severity keep API order (sort is stable), which is deterministic per run.
 */
const sortFindingsBySeverity = (allFindings: EvalFinding[], showDuplicates: boolean): EvalFinding[] => {
  const findings = showDuplicates ? allFindings : allFindings.filter((f) => !f.superseded_by);
  return [...findings].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
};

const matchesFilter = (finding: EvalFinding, filter: FindingsFilter): boolean => {
  if (filter === 'open') return !finding.resolution;
  if (filter === 'reviewed') return Boolean(finding.resolution);
  return true;
};

const groupCountLabel = ({ total, open }: TierGroup): string => {
  if (open === total) return `${total} found`;
  if (open === 0) return 'done';
  return `${open} left`;
};

/**
 * The Contract Guidance drawer. Three bodies, one at a time: the intro (no
 * scan yet), scan progress (a run in flight), and results (suggestions
 * grouped by tier). Each keeps a fixed footer with the advice notice and the
 * one action that fits the state. Internal tooling — the run switcher, config
 * picker, grading, duplicates and checklist coverage — is layered on the same
 * shell and hidden from brokers.
 */
export const PbqaEvalView: FC<PbqaEvalViewProps> = ({
  runs,
  runsLoading,
  detail,
  detailLoading,
  detailError,
  onRetryDetail,
  configs,
  configsLoading,
  championConfigId,
  budget,
  guidanceBlock,
  isLocked = false,
  canFocusBlock = true,
  activeRunId,
  activeBlockId,
  isStarting,
  isCancelling,
  isGrading,
  isResolving,
  isStartingResolutionCheck,
  showEvalControls,
  canToggleAdminMode,
  isAdminMode,
  onToggleAdminMode,
  onClose,
  onSelectRun,
  onStart,
  onCancel,
  onStartResolutionCheck,
  onGrade,
  onResolve,
  onFocusBlock,
  blockNameById,
  sectionNameById,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [filter, setFilter] = useState<FindingsFilter>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // "Expand all" suspends the single-expand accordion — with everything open,
  // opening one more card must not slam the others shut.
  const [isExpandAll, setIsExpandAll] = useState(false);
  // Closed findings sink to the bottom of their tier — except the one the
  // reviewer just closed (or reopened), which holds its place until they
  // collapse it or open another card. Otherwise the card they are looking
  // at leaps away on the click.
  const [pinned, setPinned] = useState<{ id: string; closed: boolean } | null>(null);

  const isRunActive = detail ? !RUN_IS_TERMINAL[detail.run.status] : false;
  // A scan that ended without succeeding. The raw error is an admin tool —
  // it is whatever AWS or the runner threw; a broker gets one plain line and
  // a way to start again, since the re-run (AI re-check) needs a completed scan.
  const runFailed = detail ? RUN_IS_TERMINAL[detail.run.status] && detail.run.status !== 'SUCCEEDED' : false;
  const failureMessage =
    detail?.run.status === 'CANCELLED'
      ? 'This scan was cancelled.'
      : 'The scan could not be completed. Please run it again.';

  const { visibleFindings, filteredFindings, groups, tierCounts, assessableCount } = useMemo(() => {
    const all = detail?.findings ?? [];
    const visible = sortFindingsBySeverity(all, showDuplicates);
    const filtered = visible.filter((f) => matchesFilter(f, filter));
    const counts: Record<FindingTier, number> = { PRIORITY: 0, IMPROVEMENT: 0, NOTE: 0 };
    for (const finding of visible) counts[TIER_BY_SEVERITY[finding.severity]] += 1;
    // Open first, closed last, severity order within each (sort is stable).
    const closedRank = (f: EvalFinding) => ((pinned?.id === f.id ? pinned.closed : Boolean(f.resolution)) ? 1 : 0);
    const tierGroups: TierGroup[] = TIER_ORDER.map((tier) => {
      const ofTier = visible.filter((f) => TIER_BY_SEVERITY[f.severity] === tier);
      return {
        tier,
        items: filtered
          .filter((f) => TIER_BY_SEVERITY[f.severity] === tier)
          .sort((a, b) => closedRank(a) - closedRank(b)),
        total: ofTier.length,
        open: ofTier.filter((f) => !f.resolution).length,
      };
    }).filter((group) => group.items.length > 0);
    return {
      visibleFindings: visible,
      filteredFindings: filtered,
      groups: tierGroups,
      tierCounts: counts,
      // What an AI resolution check would assess (see `isAssessableFinding`):
      // open and reviewed alike, independent of the duplicates toggle.
      assessableCount: all.filter(isAssessableFinding).length,
    };
  }, [detail, filter, pinned, showDuplicates]);

  // First card per block carries the scroll anchor used when the drawer is
  // opened from a block's finding-count pill.
  const anchorFindingIdByBlock = useMemo(() => {
    const anchors = new Map<string, string>();
    for (const finding of visibleFindings) {
      if (finding.block_id && !anchors.has(finding.block_id)) {
        anchors.set(finding.block_id, finding.id);
      }
    }
    return anchors;
  }, [visibleFindings]);

  // step_key → label, for the lens chip on finding cards.
  const stepLabelByKey = useMemo(
    () => new Map((detail?.steps ?? []).map((step) => [step.step_key, step.step_label])),
    [detail],
  );

  // Number of consolidation-merged duplicates, and a finding id → title map so
  // a revealed duplicate card can show which finding it was merged into.
  const duplicateCount = useMemo(() => (detail?.findings ?? []).filter((f) => f.superseded_by).length, [detail]);
  const findingTitleById = useMemo(() => new Map((detail?.findings ?? []).map((f) => [f.id, f.title])), [detail]);

  // Audit coverage registers, plus the two joins that let checks and findings
  // reference each other. Both live in this one response, so no extra fetch.
  const checklistRegisters = useMemo(() => buildChecklistRegisters(detail?.steps ?? []), [detail]);

  const checkTextById = useMemo(() => {
    const texts = new Map<string, string>();
    for (const register of checklistRegisters) {
      for (const result of register.results) {
        if (result.check_text) texts.set(result.check_id, result.check_text);
      }
    }
    return texts;
  }, [checklistRegisters]);

  // A failed check's "View finding" must land on a VISIBLE card. When
  // consolidation absorbs the check's own finding into a (usually lens)
  // canonical, follow superseded_by to that canonical rather than dropping
  // the link — a canonical check-finding still wins when one exists.
  const findingIdByCheckId = useMemo(() => {
    const ids = new Map<string, string>();
    for (const finding of detail?.findings ?? []) {
      if (finding.check_id && !finding.superseded_by && !ids.has(finding.check_id)) {
        ids.set(finding.check_id, finding.id);
      }
    }
    for (const finding of detail?.findings ?? []) {
      if (finding.check_id && finding.superseded_by && !ids.has(finding.check_id)) {
        ids.set(finding.check_id, finding.superseded_by);
      }
    }
    return ids;
  }, [detail]);

  // Reading a finding is not a request to move the contract viewer: expanding
  // a card only opens it. Jumping to the contract stays explicit — the card's
  // "Scroll to Block" and the inline block references.
  const handleToggleExpand = (findingId: string) => {
    setPinned(null);
    setExpandedIds((previous) => {
      // Accordion: outside expand-all mode, opening a card closes the last one.
      const next = new Set(isExpandAll ? previous : []);
      if (previous.has(findingId)) {
        next.delete(findingId);
      } else {
        next.add(findingId);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    setPinned(null);
    setExpandedIds(new Set(visibleFindings.map((finding) => finding.id)));
    setIsExpandAll(true);
  };

  const handleCollapseAll = () => {
    setPinned(null);
    setExpandedIds(new Set());
    setIsExpandAll(false);
  };

  const scrollToFinding = (findingId: string) => {
    containerRef.current
      ?.querySelector(`[data-testid="pbqa-finding-${findingId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // When the drawer is opened from a block's finding-count pill, scroll the
  // drawer's own list to that block's first (most severe) finding. The
  // reverse flow — jumping from a finding to the block in the contract
  // viewer — is handled by the controller via the entity store's highlight
  // mechanism, so we intentionally do not move the contract viewer from here.
  useEffect(() => {
    if (!activeBlockId || !containerRef.current) return;
    const anchorFindingId = anchorFindingIdByBlock.get(activeBlockId);
    if (!anchorFindingId) return;
    // Scrolling to a collapsed row would show a title and nothing else, so
    // the anchor finding opens too.
    setExpandedIds((previous) => (isExpandAll ? new Set(previous).add(anchorFindingId) : new Set([anchorFindingId])));
    const target = containerRef.current.querySelector(`[data-testid="pbqa-finding-${anchorFindingId}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on anchor change only; `isExpandAll` must not re-trigger the scroll
  }, [activeBlockId, anchorFindingIdByBlock]);

  const lastRunStamp = detail ? formatDateTime(detail.run.completed_at ?? detail.run.created_at) : '';

  // "Re-run guidance scan" is the AI re-check. The reason it is off, if any,
  // is the button's tooltip; while a check runs the button reports that. It
  // is a guidance run like any other, so the same gate applies (stage, form,
  // budget), and with nothing edited since the last scan or re-check there is
  // nothing for it to assess — the server would 409 on exactly that.
  const latestCheck = detail?.resolution_checks?.[0];
  const rerunBlock = detail
    ? (describeRerunBlock(detail.run, latestCheck, assessableCount) ??
      guidanceBlock?.body ??
      (detail.has_unchecked_changes === false ? 'The contract has not been updated since the last run.' : undefined))
    : undefined;
  const isRechecking = Boolean(latestCheck && !RUN_IS_TERMINAL[latestCheck.status]);
  // The per-tier counts live in the header, but only over the results they describe.
  const showTierCounts = Boolean(detail) && !isRunActive && !isRechecking;
  // Edits since the last scan or re-check: the suggestions may no longer fit.
  const isStale = detail?.has_unchecked_changes === true;

  const renderFindingCard = (finding: EvalFinding) => (
    <FindingCard
      key={finding.id}
      finding={finding}
      isActive={Boolean(activeBlockId) && finding.block_id === activeBlockId}
      isExpanded={expandedIds.has(finding.id)}
      isGrading={isGrading}
      isResolving={isResolving}
      isLocked={isLocked}
      canFocusBlock={canFocusBlock}
      showEvalControls={showEvalControls}
      blockNameById={blockNameById}
      sectionNameById={sectionNameById}
      stepLabelByKey={stepLabelByKey}
      findingTitleById={findingTitleById}
      checkTextById={checkTextById}
      onToggleExpand={handleToggleExpand}
      onFocusBlock={onFocusBlock}
      onGrade={(findingId, grade, note) => detail && onGrade(detail.run.id, findingId, grade, note)}
      onResolve={(findingId, resolution, note) => {
        if (!detail) return;
        // Pin only on a lifecycle change (close or reopen): saving a reason on
        // an already-closed card must not re-pin it as closed and sink it
        // while the reviewer is still on it.
        const wasClosed = Boolean(finding.resolution);
        if (wasClosed !== Boolean(resolution)) setPinned({ id: findingId, closed: wasClosed });
        onResolve(detail.run.id, findingId, resolution, note);
      }}
    />
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white" data-testid="pbqa-drawer">
      <header className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex min-w-0 flex-col">
            <h2 className="text-base font-medium whitespace-nowrap text-gray-900">Contract Guidance</h2>
            <Show when={Boolean(isAdminMode)}>
              <span className="text-xs text-gray-500" data-testid="pbqa-admin-mode-pill">
                Eval admin tools
              </span>
            </Show>
          </div>
          <Show when={showTierCounts}>
            <TierCountPills counts={tierCounts} />
          </Show>
        </div>
        <div className="flex items-center gap-1">
          {/*
            Internal staff only: layer the eval admin tools over the broker
            view. Visual only — the caller's API access is unchanged.
          */}
          <Show when={Boolean(canToggleAdminMode)}>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-gray-500"
              onClick={onToggleAdminMode}
              data-testid="pbqa-toggle-admin-mode"
              aria-label={isAdminMode ? 'Hide the eval admin tools' : 'Show the eval admin tools'}
              title={isAdminMode ? 'Hide the eval admin tools' : 'Show the eval admin tools'}
            >
              {isAdminMode ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
            </Button>
          </Show>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-gray-500"
            onClick={onClose}
            data-testid="pbqa-close-button"
            aria-label="Close"
          >
            <X aria-hidden />
          </Button>
        </div>
      </header>

      {/*
        Picking between runs is an admin tool — a broker reads the latest
        review of their contract, which the controller has already selected.
      */}
      <Show when={showEvalControls && !runsLoading && runs.length > 0}>
        <div className="border-b border-gray-200 px-4 py-2">
          <RunSwitcher runs={runs} activeRunId={activeRunId} onSelect={onSelectRun} />
        </div>
      </Show>

      <ShowFirstMatching>
        {/* A failed detail fetch must land somewhere: the spinner branch below would otherwise match forever. */}
        <Show when={!detail && Boolean(detailError)}>
          <div
            className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center"
            data-testid="pbqa-detail-error"
          >
            <p className="text-[13px] text-gray-600">This review could not be loaded.</p>
            <Show when={Boolean(onRetryDetail)}>
              <Button
                variant="outline"
                size="sm"
                className="text-gray-700"
                onClick={onRetryDetail}
                data-testid="pbqa-detail-retry"
              >
                Try again
              </Button>
            </Show>
          </div>
        </Show>

        <Show when={!detail && (runsLoading || detailLoading || runs.length > 0)}>
          <div className="flex flex-1 items-center justify-center py-6">
            <Spinner />
          </div>
        </Show>

        <Show when={!detail}>
          <>
            <div className="flex-1 overflow-y-auto">
              <IntroPanel block={guidanceBlock} />
            </div>
            <footer className={footerClasses}>
              <AdviceNotice variant="intro" />
              <StartEvalRunForm
                configs={configs}
                isLoading={configsLoading}
                isStarting={isStarting}
                budget={budget}
                label="Run guidance scan"
                showConfigPicker={showEvalControls}
                championConfigId={championConfigId}
                disabledReason={guidanceBlock?.body}
                onStart={onStart}
              />
            </footer>
          </>
        </Show>

        <Show when={Boolean(detail) && isRunActive}>
          {detail && (
            <>
              <div className="flex-1 overflow-y-auto">
                <ScanProgress detail={detail} />
              </div>
              <footer className={footerClasses}>
                <AdviceNotice variant="intro" />
                {/* Cancelling a run is an admin tool. */}
                <Show when={showEvalControls}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full text-gray-700"
                    disabled={isCancelling}
                    onClick={() => onCancel(detail.run.id)}
                    data-testid="pbqa-cancel-run"
                  >
                    Cancel
                  </Button>
                </Show>
              </footer>
            </>
          )}
        </Show>

        {/* The re-check takes the body over, like the first scan does. */}
        <Show when={Boolean(detail) && isRechecking}>
          {latestCheck && (
            <>
              <div className="flex-1 overflow-y-auto">
                <RecheckProgress check={latestCheck} />
              </div>
              <footer className={footerClasses}>
                <AdviceNotice />
              </footer>
            </>
          )}
        </Show>

        <Show when={Boolean(detail)}>
          {detail && (
            <>
              <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3">
                <FindingsToolbar
                  filter={filter}
                  onFilterChange={(next) => {
                    setPinned(null);
                    setFilter(next);
                  }}
                  onExpandAll={handleExpandAll}
                  onCollapseAll={handleCollapseAll}
                />
                <Show when={runFailed}>
                  <p
                    className="bg-error-50 text-error-700 rounded-md px-3 py-2 text-xs"
                    role="alert"
                    data-testid="pbqa-run-failed"
                  >
                    {showEvalControls && detail.run.error_message ? detail.run.error_message : failureMessage}
                  </p>
                </Show>
              </div>

              <div className="flex flex-1 flex-col overflow-y-auto bg-gray-50" ref={containerRef}>
                {/* A failed scan already explains itself in the alert above. */}
                <Show when={visibleFindings.length === 0 && !runFailed}>
                  <p className="px-6 py-10 text-center text-[13px] text-gray-500" data-testid="pbqa-no-findings">
                    No suggestions for this contract.
                  </p>
                </Show>
                <Show when={visibleFindings.length > 0 && filteredFindings.length === 0}>
                  <p className="px-6 py-10 text-center text-[13px] text-gray-500" data-testid="pbqa-filter-empty">
                    {filter === 'open'
                      ? 'Every suggestion has been reviewed.'
                      : 'No suggestions have been reviewed yet.'}
                  </p>
                </Show>

                {groups.map((group) => (
                  <section key={group.tier} data-testid={`pbqa-group-${group.tier}`}>
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                      <h3 className={cn('text-sm font-semibold', tierClasses[group.tier].text)}>
                        {TIER_LABEL[group.tier]}
                      </h3>
                      <span
                        className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', tierClasses[group.tier].pill)}
                        data-testid={`pbqa-group-count-${group.tier}`}
                      >
                        {groupCountLabel(group)}
                      </span>
                    </div>
                    <ul>{group.items.map(renderFindingCard)}</ul>
                  </section>
                ))}

                {/* Cluster quality is a debugging aid, not something a broker triages. */}
                <Show when={showEvalControls && duplicateCount > 0}>
                  <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-1">
                    <span className="text-[11px] text-gray-500">
                      {duplicateCount} duplicate{duplicateCount === 1 ? '' : 's'} merged by consolidation
                    </span>
                    <button
                      type="button"
                      data-testid="pbqa-toggle-duplicates"
                      onClick={() => setShowDuplicates((v) => !v)}
                      className="text-primary-600 hover:text-primary-700 shrink-0 text-[11px] font-medium"
                    >
                      {showDuplicates ? 'Hide duplicates' : 'Show duplicates'}
                    </button>
                  </div>
                </Show>

                {/* Audit coverage — "was everything on the checklist assessed?" — is an admin tool for now. */}
                <Show when={showEvalControls}>
                  <ChecklistSection
                    registers={checklistRegisters}
                    findingIdByCheckId={findingIdByCheckId}
                    onViewFinding={scrollToFinding}
                  />
                </Show>
              </div>

              <footer className={footerClasses}>
                <AdviceNotice>
                  <Show when={isStale}>
                    <p className="text-gray-600" data-testid="pbqa-stale-notice">
                      Suggestions may be out of date.
                      <br />
                      Last run: {lastRunStamp}
                    </p>
                  </Show>
                  <ResolutionCheck latestCheck={latestCheck} />
                </AdviceNotice>
                {/*
                  A disabled button swallows hover, so the tooltip anchors on a
                  wrapper. Enabled, there is no tooltip to show.
                */}
                <Show when={!runFailed}>
                  <Tooltip content={rerunBlock} disabled={!rerunBlock}>
                    <span className="block">
                      <Button
                        size="lg"
                        className="w-full"
                        disabled={Boolean(rerunBlock) || isStartingResolutionCheck}
                        onClick={() => onStartResolutionCheck(detail.run.id)}
                        data-testid="pbqa-rerun-button"
                      >
                        <Show when={isStartingResolutionCheck}>
                          <LoaderCircle className="animate-spin" aria-hidden />
                        </Show>
                        Re-run guidance scan
                      </Button>
                    </span>
                  </Tooltip>
                </Show>
                {/* After a failed scan the broker's primary action is a fresh scan of the champion. */}
                <Show when={runFailed && !showEvalControls}>
                  <StartEvalRunForm
                    configs={configs}
                    isLoading={configsLoading}
                    isStarting={isStarting}
                    budget={budget}
                    label="Run guidance scan"
                    championConfigId={championConfigId}
                    disabledReason={guidanceBlock?.body}
                    onStart={onStart}
                  />
                </Show>
                {/* Admin tool: a fresh scan with any config and version. */}
                <Show when={showEvalControls}>
                  <StartEvalRunForm
                    configs={configs}
                    isLoading={configsLoading}
                    isStarting={isStarting}
                    budget={budget}
                    label="Run new scan"
                    showConfigPicker
                    championConfigId={championConfigId}
                    disabledReason={guidanceBlock?.body}
                    onStart={onStart}
                  />
                </Show>
              </footer>
            </>
          )}
        </Show>
      </ShowFirstMatching>
    </div>
  );
};
